import { AbstractControl, FormGroup } from '@angular/forms';
import { Antrag } from '../types';
import {
  BaseDynamicFormElement,
  CheckboxElement,
  HeaderElement,
  HtmlElement,
  ParagraphElement,
} from '@ebeihilfe/forms';
import { Deferred, EinreichungComponents } from '@ebeihilfe/core';
import { EinreichungStepResultType } from '../interfaces';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

export type ConfirmationText = 'header' | 'text' | 'html';

export interface ConfirmationConfig {
  content: string;
  typeOfText: ConfirmationText;
}

export abstract class ConfirmationStepBase {
  public antrag!: Antrag;
  public name = EinreichungComponents.Confirmation;
  public work!: Deferred<EinreichungStepResultType>;
  private _fields!: BaseDynamicFormElement<string>[];
  protected abstract fieldConfig: ConfirmationConfig[];

  public get fields(): BaseDynamicFormElement<string>[] {
    if (this._fields?.length >= 1) {
      return this._fields as BaseDynamicFormElement<string>[];
    }

    this._fields = this.transformFields(this.fieldConfig);
    return this._fields as BaseDynamicFormElement<string>[];
  }

  public goOneStepBack(): void {
    this.work.resolve({
      result: this.antrag,
      shouldProceed: false,
      goBack: true,
    });
  }

  public hasUi(): boolean {
    return true;
  }

  public canHandle(): boolean | Promise<boolean> {
    return true;
  }

  public destroy(): Promise<void> {
    return Promise.resolve();
  }

  public submit(confirmationForm: FormGroup): void {
    const readAll: AbstractControl<boolean> | null = confirmationForm.get('readAll');
    if (readAll?.value !== true) {
      readAll?.markAsTouched();
      return;
    }

    this.work.resolve({
      result: this.antrag,
      shouldProceed: true,
    });
  }

  protected createCheckBox(): BaseDynamicFormElement<boolean> {
    return new CheckboxElement({
      key: 'readAll',
      value: false,
      label: _('validation.label.readAll'),
      required: true,
      requiredTrue: true,
      errors: [
        {
          errorMessage: _('validation.error.readAll'),
          errorCode: 'required',
        },
      ],
    });
  }

  protected transformFields(config: ConfirmationConfig[]): BaseDynamicFormElement<string>[] {
    return [
      ...config.map((item: ConfirmationConfig) => this.transformByType(item)),
      this.createCheckBox(),
    ] as BaseDynamicFormElement<string>[];
  }

  private createHeading(textToShow: string): BaseDynamicFormElement<string> {
    return new HeaderElement({
      key: `heading_${this.getRandomNumber()}`,
      label: textToShow,
    });
  }

  private createHTMLBlock(textToShow: string): BaseDynamicFormElement<string> {
    return new HtmlElement({
      key: `text_${this.getRandomNumber()}`,
      label: textToShow,
    });
  }

  private createTextBlock(textToShow: string): BaseDynamicFormElement<string> {
    return new ParagraphElement({
      key: `text_${this.getRandomNumber()}`,
      label: textToShow,
    });
  }

  private getRandomNumber(): string {
    return window.crypto.getRandomValues(new Uint8Array(1)).toString();
  }

  private transformByType(confirmationConfig: ConfirmationConfig): BaseDynamicFormElement<string> {
    switch (confirmationConfig.typeOfText) {
      case 'header':
        return this.createHeading(confirmationConfig.content);
      case 'html':
        return this.createHTMLBlock(confirmationConfig.content);
      case 'text':
      default:
        return this.createTextBlock(confirmationConfig.content);
    }
  }
}
